/**
 * ChamadoCommentApplyCard — cartão de SUGESTÃO de comentário do @Atendente.
 *
 * O Atendente propôs um comentário para um chamado existente (assessoria, MR-5).
 * A ESCRITA nunca é feita pelo Atendente: aqui um HUMANO com permissão de
 * chamados aplica com 1 clique — o backend grava o comentário como ação dele,
 * gated pela permissão de chamados no tenant DO CHAMADO e auditado.
 *
 * Gate de UX (o backend re-valida): botão "Aplicar" só habilitado para quem tem
 * chamados:comment (ou chamados:request). Sem permissão, vê a sugestão sem botão.
 *
 * TASK-11421: o comentário é INTERNO por padrão (só a equipe vê) e o cartão diz
 * isso antes do clique. Público só quando a proposta marca `interno: false`,
 * e aí o cartão avisa que o solicitante recebe e-mail.
 */
import { Badge, Button, Card, Group, Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { formatApiError } from '@/api/errors'
import { IconMessagePlus } from '@tabler/icons-react'
import { useCan } from '@/store/auth'
import { useApplyChamadoAction, useDeclineAction } from '@/hooks/useChat'
import type { ProposedAction } from './ActionProposalCard'

interface Props {
  conversationId: number
  messageId: number
  proposedAction: ProposedAction
}

const STATUS_LABELS: Record<ProposedAction['status'], string> = {
  pending: 'Pendente',
  executed: 'Aplicado',
  failed: 'Falhou',
  declined: 'Recusado',
}
const STATUS_COLORS: Record<ProposedAction['status'], string> = {
  pending: 'yellow',
  executed: 'teal',
  failed: 'red',
  declined: 'gray',
}

/**
 * INTERNO salvo quando a proposta diz, explicitamente, que não é. `params` vem
 * do JSON da proposta, então o booleano pode chegar como bool ou string — e a
 * ausência (proposta gravada antes da TASK-11421) é interna, como no servidor.
 */
export function comentarioEhInterno(params: Record<string, unknown>): boolean {
  const cru = params.interno
  return cru !== false && cru !== 'false'
}

export function ChamadoCommentApplyCard({ conversationId, messageId, proposedAction }: Props) {
  const { status, params } = proposedAction
  const can = useCan()
  const canApply = can('chamados', 'comment') || can('chamados', 'request')

  const applyMutation = useApplyChamadoAction(conversationId)
  const declineMutation = useDeclineAction(conversationId)

  const isPending = status === 'pending'
  const ticketRef = params.task_request_id ? `#${params.task_request_id}` : 'chamado'
  const comment = params.comment ?? ''
  const interno = comentarioEhInterno(params)

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{ borderLeft: '3px solid var(--mantine-color-teal-6, #0d9488)', maxWidth: 480 }}
    >
      <Group gap="xs" mb="xs" wrap="nowrap">
        <IconMessagePlus size={16} />
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {`Comentário sugerido para o ${ticketRef}`}
        </Text>
        <Badge size="xs" color={interno ? 'gray' : 'orange'} variant="outline">
          {interno ? 'Interno' : 'Público'}
        </Badge>
        <Badge size="xs" color={STATUS_COLORS[status]} variant="light">
          {STATUS_LABELS[status]}
        </Badge>
      </Group>

      <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }} mb="xs">
        {comment}
      </Text>

      <Text size="xs" c="dimmed" mb="xs">
        {interno
          ? 'Só a equipe do chamado vê este comentário.'
          : 'O solicitante vê este comentário e recebe e-mail.'}
      </Text>

      {isPending && (
        <Group gap="xs" mt="xs" wrap="nowrap">
          <Tooltip
            label="Requer permissão de chamados"
            disabled={canApply}
            withArrow
            position="top"
          >
            <div>
              <Button
                size="xs"
                color="inovareTeal"
                variant="filled"
                disabled={!canApply}
                loading={applyMutation.isPending}
                onClick={() =>
                  applyMutation.mutate(
                    { messageId },
                    {
                      onError: (err: unknown) => {
                        const raw = formatApiError(err, 'Falha ao aplicar o comentário.')
                        const msg: Record<string, string> = {
                          sem_permissao_de_comentario_interno:
                            'Comentário interno só pode ser aplicado por quem atende o chamado.',
                          sem_permissao_de_chamados: 'Você não tem permissão de chamados neste ambiente.',
                        }
                        notifications.show({ color: 'red', title: 'Erro', message: msg[raw] ?? raw })
                      },
                    },
                  )
                }
              >
                Aplicar comentário
              </Button>
            </div>
          </Tooltip>
          <Button
            size="xs"
            color="red"
            variant="light"
            loading={declineMutation.isPending}
            onClick={() => declineMutation.mutate({ messageId })}
          >
            Recusar
          </Button>
        </Group>
      )}
    </Card>
  )
}
